import React, { useLayoutEffect } from 'react';
import * as Yup from "yup";
import { Col, Row, Input, Label, Button, Form } from "reactstrap";
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Field, FieldProps, Formik } from 'formik';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import { errorToast, successToast } from 'Components/Toasts';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import apiGlobal from 'global/api.global';
import { loadSTSOngoingObject } from 'VesselMaster/vesselMaster.hooks';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import Loading from 'Components/Loading';
import { queryClient } from 'react-query/queryClient';
import { errResponse } from 'GenericForms/Helper';
import env from 'environment/env';
import { STSOperations } from 'shared/constants';

interface STSOperationType {
    record: any,
    lastRecord: any,
    VesselID: number,
    ReportID: number,
    activeTab: number,
    toggleTab: any
}

const STSOperationComponent = ({
    record,
    lastRecord,
    VesselID,
    ReportID,
    activeTab,
    toggleTab
}: STSOperationType) => {
    /** Queries */
    /** STS ongoing object used for edit */
    const { data: STSOngoingObject, isLoading: STSOngoingObjectIsLoading, isError: STSOngoingObjectIsError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.STSOngoingObject.key, VesselID],
            async () => {
                return await loadSTSOngoingObject(VesselID);
            },
            { staleTime: Infinity }
        )
    /** Queries end */

    /** Assign values to initial object of STS Loading */
    const getInitailLoadingValues = () => {
        if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.LOADING)[0]?.id > 0) {
            return STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.LOADING)[0];
        } else {
            return {
                loading: false,
                sts_start_time: null,
                sts_end_time: null,
                ongoing_status: false,
                vessel: VesselID,
                sts_start_report: null,
                sts_end_report: null,
                user_approved: false,
                class_approved: false,
                sts_operation: STSOperations.LOADING,
            }
        }
    }

    /** Assign values to initial object of STS Discharging */
    const getInitailDischargingValues = () => {
        if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0) {
            return STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.DISCHARGING)[0];
        } else {
            return {
                dicharging: false,
                sts_start_time: null,
                sts_end_time: null,
                ongoing_status: false,
                vessel: VesselID,
                sts_start_report: null,
                sts_end_report: null,
                user_approved: false,
                class_approved: false,
                sts_operation: STSOperations.DISCHARGING,
            }
        }
    }

    /** Assign values to initial object of STS Port Discharging */
    const getInitialPortDischargingValues = () => {
        if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0) {
            return STSOngoingObject?.filter((item: any) => item.sts_operation === STSOperations.PORT_DISCHARGING)[0];
        } else {
            return {
                discharging: false,
                sts_start_time: null,
                sts_end_time: null,
                ongoing_status: false,
                vessel: VesselID,
                sts_start_report: null,
                sts_end_report: null,
                user_approved: false,
                class_approved: false,
                sts_operation: STSOperations.DISCHARGING,
            }
        }
    }

    /** UseEffect */
    useLayoutEffect(() => {
        STSOperationFormik.initialValues.stsLoading = getInitailLoadingValues();
        STSOperationFormik.initialValues.stsDischarging = getInitailDischargingValues();
        STSOperationFormik.initialValues.portDischarging = getInitialPortDischargingValues()
    }, [STSOngoingObject])
    /** UseEffect end */

    const parseDateString = (dateString: any) => {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? null : date;
    };

    /** STS Operation formik object */
    const STSOperationFormik = {
        initialValues: {
            stsLoading: getInitailLoadingValues(),
            stsDischarging: getInitailDischargingValues(),
            portDischarging: getInitialPortDischargingValues()
        },
        validationSchema: Yup.object().shape({
            stsLoading: Yup.object({
                sts_start_time: Yup.date().min(
                    parseDateString(
                        (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                            item.sts_operation === STSOperations.LOADING)[0]?.id > 0)
                            ? '2022-12-31T12:00:00' : lastRecord?.reporting_time_utc
                    ),
                    `${commonValidationMessages.minDateLastRecord}`
                )
                    .max(new Date(record?.reporting_time_utc ?? '31-12-2022T12:00:00'),
                        `${commonValidationMessages.maxDateCurrentRecord}`).nullable(),
                sts_end_time: Yup.date().min(new Date(lastRecord?.reporting_time_utc ?? '31-12-2022T12:00:00'),
                    `${commonValidationMessages.minDateLastRecord}`)
                    .max(new Date(record?.reporting_time_utc ?? '31-12-2022T12:00:00'),
                        `${commonValidationMessages.maxDateCurrentRecord}`).nullable(),
            }),
            stsDischarging: Yup.object({
                sts_start_time: Yup.date().min(
                    parseDateString(
                        (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                            item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0)
                            ? '2022-12-31T12:00:00'
                            : lastRecord?.reporting_time_utc ?? '2022-12-31T12:00:00'
                    ),
                    `${commonValidationMessages.minDateLastRecord}`
                )
                    .max(new Date(record.reporting_time_utc)).nullable(),
                sts_end_time: Yup.date().min(new Date(lastRecord.reporting_time_utc ?? '31-12-2022'))
                    .max(new Date(record.reporting_time_utc)).nullable(),
            }),
            portDischarging: Yup.object({
                sts_start_time: Yup.date().min(
                    parseDateString(
                        (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                            item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0)
                            ? '2022-12-31T12:00:00'
                            : lastRecord?.reporting_time_utc ?? '2022-12-31T12:00:00'
                    ),
                    `${commonValidationMessages.minDateLastRecord}`
                )
                    .max(new Date(record.reporting_time_utc)).nullable(),
                sts_end_time: Yup.date().min(new Date(lastRecord.reporting_time_utc ?? '31-12-2022'))
                    .max(new Date(record.reporting_time_utc)).nullable(),
            }),
        })
    }
    const handleSTSLoadingStartChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('stsLoading.ongoing_status', true);
        form.setFieldValue('stsLoading.sts_start_report', ReportID);
    }

    const handleSTSLoadingEndChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('stsLoading.ongoing_status', false);
        form.setFieldValue('stsLoading.sts_end_report', ReportID);
    }

    const handleSTSDischargingStartChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('stsDischarging.ongoing_status', true);
        form.setFieldValue('stsDischarging.sts_start_report', ReportID);
    }

    const handleSTSDischargingEndChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('stsDischarging.ongoing_status', false);
        form.setFieldValue('stsDischarging.sts_end_report', ReportID);
    }

    const handlePortDischargingStartChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('portDischarging.ongoing_status', true);
        form.setFieldValue('portDischarging.sts_start_report', ReportID);
    }

    const handlePortDischargingEndChanges = (e: any, form: any, values: any) => {
        form.setFieldValue('portDischarging.ongoing_status', false);
        form.setFieldValue('portDischarging.sts_end_report', ReportID);
    }
    return (
        <>
            <div className="text-center mb-4">
                <Row>
                    <Col>
                        <h4 className="page_title pos-start mb-0">STS Operation</h4>
                        <p className="card-title-desc pos-start">All readings since last report</p>
                    </Col>
                    <Col>
                        <ReportDetailsHeader />
                    </Col>
                </Row>
            </div>
            {(STSOngoingObjectIsLoading) &&
                <Loading message='Loading required data!' />}
            {STSOngoingObjectIsError && getInitailLoadingValues() && getInitailDischargingValues()}
            {!STSOngoingObjectIsLoading &&
                <>
                    <Formik
                        onSubmit={(values, actions) => {
                            actions.setSubmitting(false);
                            const handleResponse = (response: any) => {
                                if (response.status === 201 || response.status === 200) {
                                    successToast("Data saved successfully!");
                                }
                            }

                            /** STS Loading submit */
                            if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                item.sts_operation === STSOperations.LOADING)[0]?.id > 0) {
                                apiGlobal.put(`/sts_operation_detail/${values?.stsLoading.id}/`, values?.stsLoading).then(res => {
                                    handleResponse(res);
                                    if (res.status === 200) {
                                        if (env?.form_validation === true) {
                                            toggleTab(activeTab + 1);
                                        }
                                        queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            } else {
                                apiGlobal.post(`/sts_operation_detail/`, values.stsLoading).then(res => {
                                    handleResponse(res);
                                    if (res.status === 201) {
                                        if (env?.form_validation === true) {
                                            toggleTab(activeTab + 1);
                                            queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                        }
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            }
                            /** STS Discharging submit */
                            if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0) {
                                apiGlobal.put(`/sts_operation_detail/${values?.stsDischarging.id}/`, values?.stsDischarging).then(res => {
                                    handleResponse(res);
                                    if (res.status === 200) {
                                        queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            } else {
                                apiGlobal.post(`/sts_operation_detail/`, values.stsDischarging).then(res => {
                                    handleResponse(res)
                                    if (res.status === 201) {
                                        queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            }
                            /** STS Port Discharging submit */
                            if (STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0) {
                                apiGlobal.put(`/sts_operation_detail/${values?.portDischarging?.id}/`, values?.portDischarging).then(res => {
                                    handleResponse(res);
                                    if (res.status === 200) {
                                        queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            } else {
                                apiGlobal.post(`/sts_operation_detail/`, values.portDischarging).then(res => {
                                    handleResponse(res)
                                    if (res.status === 201) {
                                        queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingObject.key);
                                    }
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                })
                            }
                        }}
                        initialValues={STSOperationFormik.initialValues}
                        validationSchema={STSOperationFormik.validationSchema}
                    >
                        {({ values, errors, handleSubmit, handleChange }:
                            { values: any, errors: any, handleSubmit: any, handleChange: any }) => (
                            <Form autoComplete="off" onSubmit={handleSubmit} noValidate>
                                <Row className="mb-5">
                                    <Col lg={3}>
                                        <div className="form-check mb-3">
                                            <Field name={'stsLoading.loading'}>
                                                {({ field, form }: FieldProps) => (
                                                    <Input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="loading"
                                                        name={field.name}
                                                        onChange={(e: any) => handleChange(e)}
                                                        defaultChecked={(STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                            item.sts_operation === STSOperations.LOADING)[0]?.id > 0) ? true : false}
                                                    />
                                                )}
                                            </Field>
                                            <Label>STS Loading<i className='bx bx-info-circle ml-2p'></i></Label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Row>
                                            <Col lg={4}>
                                                <Label className='asteric mb-0'>Start(UTC)<i className='bx bx-info-circle ml-2p'></i></Label><br />
                                                {
                                                    STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                        item.sts_operation === STSOperations.LOADING)[0]?.id > 0 ?
                                                        <Col lg={7}>
                                                            <Field
                                                                type="text"
                                                                className="mt-0 form-control"
                                                                name='stsLoading.sts_start_time'
                                                                value={
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.LOADING)[0].sts_start_time.toString().substring(0, 10) + ' ' +
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.LOADING)[0].sts_start_time.toString().substring(11, 16)
                                                                }
                                                                disabled
                                                            />
                                                        </Col> :
                                                        (
                                                            <>
                                                                <Field name={'stsLoading.sts_start_time'}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <input
                                                                            type="datetime-local"
                                                                            name={field.name}
                                                                            className='datetimepicker text-uppercase mt-0'
                                                                            disabled={values?.stsLoading.loading === true ? false : true}
                                                                            min={lastRecord ? lastRecord.reporting_time_utc.toString().substring(0, 16) : '31-12-2022'}
                                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handleSTSLoadingStartChanges(e, form, values);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                {errors && errors.stsLoading &&
                                                                    <Label className='error'>{errors?.stsLoading?.sts_start_time}</Label>
                                                                }
                                                            </>
                                                        )
                                                }
                                            </Col>
                                            <Col lg={5}>
                                                <Label className='asteric mb-0'>End(UTC)<i className='bx bx-info-circle ml-2p'></i></Label><br />
                                                <Field name={'stsLoading.sts_end_time'}>
                                                    {({ field, form }: FieldProps) => (
                                                        <input
                                                            type="datetime-local"
                                                            name={field.name}
                                                            className='datetimepicker text-uppercase mt-0'
                                                            min={lastRecord && lastRecord?.reporting_time_utc?.toString().substring(0, 16)}
                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                            disabled={values?.stsLoading.loading === true || (STSOngoingObject?.length > 0 &&
                                                                STSOngoingObject?.filter((item: any) =>
                                                                    item.sts_operation === STSOperations.LOADING)[0]?.id > 0)
                                                                ? false : true
                                                            }
                                                            onChange={(e: any) => {
                                                                handleChange(e);
                                                                handleSTSLoadingEndChanges(e, form, values);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                {errors && errors.stsLoading &&
                                                    <Label className='error'>{errors?.stsLoading?.sts_end_time}</Label>
                                                }
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row className="mb-5">
                                    <Col lg={3}>
                                        <div className="form-check mb-3">
                                            <Field name={'stsDischarging.discharging'}>
                                                {({ field }: FieldProps) => (
                                                    <Input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="discharging"
                                                        name={field.name}
                                                        onChange={(e: any) => handleChange(e)}
                                                        defaultChecked={(STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                            item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0) ? true : false}
                                                    />
                                                )}
                                            </Field>
                                            <Label>STS Discharging<i className='bx bx-info-circle ml-2p'></i></Label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Row>
                                            <Col lg={4}>
                                                <Label className='asteric mb-0'>Start(UTC)<i className='bx bx-info-circle ml-2p'></i></Label><br />
                                                {
                                                    STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                        item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0 ?
                                                        <Col lg={7}>
                                                            <Field
                                                                type="text"
                                                                className="mt-0 form-control"
                                                                name='stsDischarging.sts_start_time'
                                                                value={
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.DISCHARGING)[0].sts_start_time.substring(0, 10) + ' ' +
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.DISCHARGING)[0].sts_start_time.substring(11, 16)}
                                                                disabled
                                                            />
                                                        </Col> :
                                                        (
                                                            <>
                                                                <Field name={'stsDischarging.sts_start_time'}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <input
                                                                            type="datetime-local"
                                                                            name={field.name}
                                                                            className='datetimepicker text-uppercase mt-0'
                                                                            disabled={values?.stsDischarging.discharging === true ? false : true}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handleSTSDischargingStartChanges(e, form, values);
                                                                            }}
                                                                            min={lastRecord && lastRecord.reporting_time_utc.toString().substring(0, 16)}
                                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                {errors && errors.stsDischarging &&
                                                                    <Label className='error'>{errors?.stsDischarging?.sts_start_time}</Label>
                                                                }
                                                            </>
                                                        )
                                                }
                                            </Col>
                                            <Col lg={5}>
                                                <Label className='asteric mb-0'>End(UTC)<i className='bx bx-info-circle ml-2p'></i></Label><br />
                                                <Field name={'stsDischarging.sts_end_time'}>
                                                    {({ field, form }: FieldProps) => (
                                                        <input
                                                            type="datetime-local"
                                                            name={field.name}
                                                            className='datetimepicker text-uppercase mb-3 mt-0'
                                                            min={lastRecord && lastRecord?.reporting_time_utc?.toString().substring(0, 16)}
                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                            disabled={(values?.stsDischarging.discharging === true || (STSOngoingObject?.length > 0 &&
                                                                STSOngoingObject?.filter((item: any) =>
                                                                    item.sts_operation === STSOperations.DISCHARGING)[0]?.id > 0)) ?
                                                                false : true
                                                            }
                                                            onChange={(e: any) => {
                                                                handleChange(e);
                                                                handleSTSDischargingEndChanges(e, form, values);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                {errors && errors.stsDischarging &&
                                                    <Label className='error'>{errors?.stsDischarging?.sts_end_time}</Label>
                                                }
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={3}>
                                        <div className="form-check mb-3">
                                            <Field name={'portDischarging.discharging'}>
                                                {({ field, form }: FieldProps) => (
                                                    <Input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="port_discharging"
                                                        name={field.name}
                                                        onChange={(e: any) => handleChange(e)}
                                                        defaultChecked={(STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                            item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0) ? true : false}
                                                    />
                                                )}
                                            </Field>
                                            <Label>Port Discharging after STS Loading<i className='bx bx-info-circle ml-2p' /></Label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Row>
                                            <Col lg={4}>
                                                <Label className='asteric mb-0'>Start(UTC)<i className='bx bx-info-circle ml-2p' /></Label><br />
                                                {
                                                    STSOngoingObject?.length > 0 && STSOngoingObject?.filter((item: any) =>
                                                        item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0 ?
                                                        <Col lg={7}>
                                                            <Field
                                                                type="text"
                                                                className="mt-0 form-control"
                                                                name='portDischarging.sts_start_time'
                                                                value={
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.PORT_DISCHARGING)[0].sts_start_time.substring(0, 10) + ' ' +
                                                                    STSOngoingObject?.filter((item: any) =>
                                                                        item.sts_operation === STSOperations.PORT_DISCHARGING)[0].sts_start_time.substring(11, 16)}
                                                                disabled
                                                            />
                                                        </Col>
                                                        :
                                                        (
                                                            <>
                                                                <Field name={'portDischarging.sts_start_time'}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <input
                                                                            type="datetime-local"
                                                                            name={field.name}
                                                                            className='datetimepicker text-uppercase mt-0'
                                                                            disabled={values?.portDischarging.discharging === true ? false : true}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handlePortDischargingStartChanges(e, form, values);
                                                                            }}
                                                                            min={lastRecord && lastRecord.reporting_time_utc.toString().substring(0, 16)}
                                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                {errors && errors.portDischarging &&
                                                                    <Label className='error'>{errors?.portDischarging?.sts_start_time}</Label>
                                                                }
                                                            </>
                                                        )
                                                }
                                            </Col>
                                            <Col lg={5}>
                                                <Label className='asteric mb-0'>End(UTC)<i className='bx bx-info-circle ml-2p'></i></Label><br />
                                                <Field name={'portDischarging.sts_end_time'}>
                                                    {({ field, form }: FieldProps) => (
                                                        <input
                                                            type="datetime-local"
                                                            name={field.name}
                                                            className='datetimepicker text-uppercase mb-3 mt-0'
                                                            min={lastRecord && lastRecord?.reporting_time_utc?.toString().substring(0, 16)}
                                                            max={record && record?.reporting_time_utc.toString().substring(0, 16)}
                                                            disabled={(values?.portDischarging.discharging === true || (STSOngoingObject?.length > 0 &&
                                                                STSOngoingObject?.filter((item: any) =>
                                                                    item.sts_operation === STSOperations.PORT_DISCHARGING)[0]?.id > 0)) ?
                                                                false : true
                                                            }
                                                            onChange={(e: any) => {
                                                                handleChange(e);
                                                                handlePortDischargingEndChanges(e, form, values);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                {errors && errors.portDischarging &&
                                                    <Label className='error'>{errors?.portDischarging?.sts_end_time}</Label>
                                                }
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                {/* <Row>
                                    <CardBody>
                                        <Row className="justify-content-center">
                                            <Col xl={10}>
                                                <div className="timeline">
                                                    <div className="timeline-container">
                                                        <div className="timeline-end">
                                                            <p>Timeline</p>
                                                        </div>
                                                        <div className="timeline-continue">
                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-user-pin text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box sts-timeline-box">
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 mb-0">
                                                                                    Departure
                                                                                </h3>
                                                                                <h6>Port: Houston</h6>
                                                                                <p className="mb-0 mt-2 pt-1">
                                                                                    25 Jun 2023 18:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-briefcase-alt-2 text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            <Row className="timeline-right">
                                                                <Col md={6}>
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-bar-chart-square text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box bg-light sts-timeline-box">
                                                                        <div className="timeline-date bg-danger text-center rounded">
                                                                            <h2><i className='mdi mdi-cancel icon_s28 text-white' /></h2>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 text-secondary">
                                                                                    Loading operation start
                                                                                </h3>
                                                                                <p className="mb-0 mt-2 pt-1 text-secondary">
                                                                                    25 Jun 2023 02:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-user-pin text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box sts-timeline-box">
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 mb-0">
                                                                                    Arrival
                                                                                </h3>
                                                                                <h6>Port: Pajaritos</h6>
                                                                                <p className="mb-0 mt-2 pt-1">
                                                                                    29 Jun 2023 18:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-briefcase-alt-2 text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-right">
                                                                <Col md={6}>
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-pie-chart-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box">
                                                                        <div className="timeline-date bg-primary text-center rounded">
                                                                            <h3 className="text-white mb-0">23</h3>
                                                                            <p className="mb-0 text-white-50">July</p>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18">
                                                                                    Timeline Event Five
                                                                                </h3>

                                                                                <p className="mb-0 mt-2 pt-1 text-muted">
                                                                                    Excepturi, obcaecati, quisquam id
                                                                                    molestias eaque asperiores voluptatibus
                                                                                    cupiditate error assumenda delectus odit
                                                                                    similique earum voluptatem Odit, itaque,
                                                                                    deserunt corporis vero ipsum nisi
                                                                                    repellat ...{" "}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-home-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box">
                                                                        <div className="timeline-date bg-primary text-center rounded">
                                                                            <h3 className="text-white mb-0">25</h3>
                                                                            <p className="mb-0 text-white-50">June</p>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18">
                                                                                    Timeline Event End
                                                                                </h3>
                                                                                <p className="mb-0 mt-2 pt-1 text-muted">
                                                                                    Suspendisse tempor porttitor elit non
                                                                                    maximus. Sed suscipit, purus in
                                                                                    convallis condimentum, risus ex
                                                                                    pellentesque sapien, vel tempor arcu
                                                                                    dolor ut est. Nam ac felis id mauris
                                                                                    fermentum nisl pharetra auctor.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-home-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                        <div className="timeline-start">
                                                            <p>End</p>
                                                        </div>
                                                        <div className="timeline-launch">
                                                            <div className="timeline-box">
                                                                <div className="timeline-text">
                                                                    <h3 className="font-size-18">
                                                                        Launched our company on 21 June 2021
                                                                    </h3>
                                                                    <p className="text-muted mb-0">
                                                                        Pellentesque sapien ut est.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Row> */}
                                <Row className="ele_row1">
                                    <div className="d-flex flex-wrap gap-5 mt-3 btn-Margin">
                                        <Button type="submit" color="primary" className="btn_size_cstm pos-end" onClick={(e) => {
                                            if (env?.form_validation === false) {
                                                toggleTab(activeTab + 1);
                                            }
                                        }}>Next <i className="bx bx-chevron-right ms-1" /></Button>
                                        <div className="d-flex flex-wrap gap-5 mt-3 btn_Margin">
                                            <Button type="button" color="primary" className="btn_size_cstm pos-start-0_75" onClick={(e) => {
                                                toggleTab(activeTab - 1);
                                            }}><i className="bx bx-chevron-left me-1" /> Previous</Button>
                                        </div>
                                    </div>
                                </Row>
                                <FormValuesDebug values={[values, errors, STSOperationFormik.initialValues]} />
                            </Form>
                        )}
                    </Formik>
                </>
            }
        </>
    )
}

export default STSOperationComponent